import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, FileText, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function DoctorDashboard() {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, patientsRes] = await Promise.all([
        api.get('/appointments'),
        api.get('/patients')
      ]);
      setAppointments(appointmentsRes.data.filter(apt => apt.status === 'scheduled').slice(0, 5));
      setPatients(patientsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="doctor-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Doctor Dashboard
          </h1>
          <p className="text-slate-600 mt-2">Manage your appointments and patients</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Appointments */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                <Calendar className="h-5 w-5 text-[#4A238F]" />
                Today's Appointments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <p className="text-slate-500 text-sm">No appointments scheduled</p>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="p-3 bg-slate-50 rounded-md border border-slate-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">Patient ID: {apt.patient_id.slice(0, 8)}</p>
                          <p className="text-sm text-slate-600 mt-1">{apt.reason}</p>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock className="h-4 w-4" />
                          {apt.appointment_time}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Patients */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                <Users className="h-5 w-5 text-[#4A238F]" />
                Recent Patients
              </CardTitle>
            </CardHeader>
            <CardContent>
              {patients.length === 0 ? (
                <p className="text-slate-500 text-sm">No patients registered</p>
              ) : (
                <div className="space-y-3">
                  {patients.map((patient) => (
                    <div key={patient.id} className="p-3 bg-slate-50 rounded-md border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{patient.full_name}</p>
                          <p className="text-sm text-slate-600">{patient.patient_number}</p>
                        </div>
                        <span className="text-xs text-slate-500">{patient.gender}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}